import { useState } from "react";
import {
  ArrowLeft,
  FileText,
  CheckSquare,
  Lightbulb,
  Download,
  Loader2,
  Check,
  X,
  Plus,
  ExternalLink,
  AlertTriangle,
  Star,
  Archive,
  MoreHorizontal,
  Sparkles,
  FolderKanban,
  Bookmark,
  Info,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Thumb } from "@/components/Thumb";
import { useItemsStore } from "@/lib/stores/itemsStore";
import { STATUS_META } from "@/components/StatusBadge";
import { splitMeta } from "@/lib/format";

const SELECTABLE_STATUSES = ["inbox", "pendiente", "importante", "aplicado"];

const DOWNLOAD_LABEL = {
  ninguna: "Guardar copia del vídeo",
  descargando: "Descargando…",
  disponible: "Copia disponible",
  no_disponible: "Copia no disponible",
};

const ToolbarButton = ({ icon: Icon, label, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`detail-toolbar-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[13px] font-medium transition-colors",
      active ? "text-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary/80",
    )}
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} fill={active ? "currentColor" : "none"} />
    <span className="hidden xl:inline">{label}</span>
  </button>
);

const ConvertCard = ({ icon: Icon, title, hint, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="rounded-lg border bg-card p-3 text-left hover:border-teal-300/60 hover:shadow-sm transition-all"
  >
    <span className="flex items-center gap-2 font-medium text-sm">
      <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} /> {title}
    </span>
    <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
  </button>
);

export const DetailPane = ({ item, onBack, onClose }) => {
  const { setStatus, addTag, removeTag, downloadItem, createAction, retryEnrich, projects } = useItemsStore();
  const [addingTag, setAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState("");

  if (!item) return null;

  const { duration, author } = splitMeta(item.meta);
  const enriched = item.analyzeStatus === "enriquecido";

  const handleAction = (type) => {
    createAction(item.id, type);
    const msgs = {
      nota: "Nota creada a partir de este contenido.",
      tarea: "Tarea creada. La verás en Tareas.",
      idea: "Idea guardada.",
    };
    toast.success(msgs[type]);
  };

  const handleDownload = () => {
    if (item.downloadStatus !== "ninguna") return;
    downloadItem(item.id);
    toast("Preparando la copia…", { description: "Descarga simulada en este prototipo." });
  };

  const submitTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t) addTag(item.id, t);
    setTagInput("");
    setAddingTag(false);
  };

  return (
    <div className="h-full flex flex-col min-h-0 bg-background" data-testid="detail-pane">
      {/* Toolbar de acciones */}
      <div className="flex items-center gap-1 px-3 h-12 border-b shrink-0">
        {onBack && (
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Volver" data-testid="detail-back-button" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        )}
        <ToolbarButton
          icon={Star}
          label="Marcar importante"
          active={item.status === "importante"}
          onClick={() => setStatus(item.id, item.status === "importante" ? "inbox" : "importante")}
        />
        <ToolbarButton
          icon={Archive}
          label="Archivar"
          onClick={() => {
            setStatus(item.id, "archivado");
            toast("Contenido archivado.");
          }}
        />
        <ToolbarButton icon={CheckSquare} label="Crear tarea" onClick={() => handleAction("tarea")} />
        <ToolbarButton icon={MoreHorizontal} label="Más" onClick={() => toast("Más acciones en la versión final.")} />
        <span className="flex-1" />
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar detalle" data-testid="detail-close-button" className="h-8 w-8">
            <X className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 md:px-6 py-5">
        <div className="max-w-3xl space-y-6">
          {/* Cabecera: título + meta a la izquierda, miniatura a la derecha */}
          <div className="flex flex-col-reverse sm:flex-row gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] text-muted-foreground">
                {item.platform}
                {author ? ` · ${author}` : ""}
              </p>
              <h2 className="mt-1 font-serif text-2xl leading-snug">{item.title}</h2>
              <p className="mt-1 text-[13px] text-muted-foreground">{duration}</p>
              <div className="mt-3 flex items-center gap-2">
                <Button asChild variant="outline" size="sm" data-testid="detail-open-source">
                  <a href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Ver en {item.platform}
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Guardar en marcadores"
                  onClick={() => toast("Guardado en tus destacados.")}
                >
                  <Bookmark className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Button>
              </div>
            </div>
            <Thumb platform={item.platform} size="hero" className="sm:w-64 shrink-0" />
          </div>

          {/* Resumen IA */}
          <div data-testid="ai-summary-block">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="flex items-center gap-1.5 text-sm font-medium text-teal-700 dark:text-teal-400">
                <Sparkles className="h-4 w-4" strokeWidth={1.75} /> Resumen generado por IA
              </span>
              <span className="rounded-full bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-400 px-2.5 py-0.5 text-[11px] font-medium">
                Resumen generado por IA
              </span>
            </div>
            {enriched ? (
              <p className="text-[15px] leading-relaxed">{item.summary}</p>
            ) : item.analyzeStatus === "enriqueciendo" ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-warning" />
                El agente está enriqueciendo este contenido…
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-warning" strokeWidth={1.75} />
                  Pendiente de enriquecer. El contenido está guardado y a salvo.
                </p>
                <Button variant="outline" size="sm" onClick={() => retryEnrich(item.id)} data-testid="retry-enrich-button">
                  Reintentar análisis
                </Button>
              </div>
            )}
          </div>

          {/* Temática / Utilidad */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg border bg-card p-3.5">
              <p className="text-sm font-semibold">Temática</p>
              <p className="mt-1 text-sm text-muted-foreground">{item.mainTopic || "—"}</p>
            </div>
            <div className="rounded-lg border bg-card p-3.5">
              <p className="text-sm font-semibold">Utilidad</p>
              <p className="mt-1 text-sm text-muted-foreground">{item.usefulness || "—"}</p>
            </div>
          </div>

          {/* Etiquetas */}
          <div>
            <p className="text-sm font-semibold mb-2">Etiquetas</p>
            <div className="flex flex-wrap gap-1.5">
              {item.tags.map((t) => (
                <span
                  key={t}
                  className="group inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-mono text-muted-foreground"
                >
                  #{t}
                  <button
                    type="button"
                    onClick={() => removeTag(item.id, t)}
                    aria-label={`Quitar etiqueta ${t}`}
                    className="opacity-40 hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {addingTag ? (
                <Input
                  autoFocus
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitTag();
                    if (e.key === "Escape") setAddingTag(false);
                  }}
                  onBlur={submitTag}
                  placeholder="nueva-etiqueta"
                  className="h-7 w-36 text-xs font-mono"
                  data-testid="add-tag-input"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingTag(true)}
                  data-testid="add-tag-button"
                  className="inline-flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-xs text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                >
                  <Plus className="h-3 w-3" /> Añadir etiqueta
                </button>
              )}
            </div>
          </div>

          {/* Estado */}
          <div>
            <p className="text-sm font-semibold mb-2">Estado</p>
            <div className="flex flex-wrap gap-1.5">
              {SELECTABLE_STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(item.id, s)}
                  data-testid={`status-option-${s}`}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    STATUS_META[s].cls,
                    item.status === s
                      ? "ring-1 ring-primary border border-primary/50"
                      : "border border-transparent opacity-70 hover:opacity-100",
                  )}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
          </div>

          {/* Convertir en trabajo */}
          <div>
            <p className="text-sm font-semibold mb-2">Convierte este contenido en</p>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
              <ConvertCard icon={FileText} title="Nota" hint="Extrae lo importante" onClick={() => handleAction("nota")} testId="create-note-button" />
              <ConvertCard icon={CheckSquare} title="Tarea" hint="Conviértelo en acción" onClick={() => handleAction("tarea")} testId="create-task-button" />
              <ConvertCard icon={Lightbulb} title="Idea" hint="Guárdalo para luego" onClick={() => handleAction("idea")} testId="save-idea-button" />
              <ConvertCard
                icon={FolderKanban}
                title="Proyecto"
                hint="Añade a un proyecto"
                onClick={() => toast.success(`Añadido a «${projects[0]?.name}».`)}
                testId="add-to-project-button"
              />
            </div>
          </div>

          {/* Más acciones */}
          <div>
            <p className="text-sm font-semibold mb-2">Más acciones</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                disabled={item.downloadStatus === "descargando" || item.downloadStatus === "no_disponible"}
                data-testid="save-copy-button"
              >
                {item.downloadStatus === "descargando" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : item.downloadStatus === "disponible" ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={1.75} />
                ) : (
                  <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                )}
                {DOWNLOAD_LABEL[item.downloadStatus]}
              </Button>
              <Button variant="outline" size="sm" onClick={() => toast("Integración disponible en la versión final.")}>
                <Send className="h-3.5 w-3.5" strokeWidth={1.75} /> Enviar a Readwise
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              La copia descarga el vídeo a tu almacenamiento para tenerlo bajo tu control (formación).
            </p>
          </div>
        </div>
      </div>

      {/* Transparencia IA (Art. 50) */}
      <div className="border-t px-4 md:px-6 py-2.5 shrink-0 flex items-center gap-2 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
        <span className="truncate">Resumen y etiquetas generados por IA. Pueden contener errores.</span>
        <button type="button" className="ml-auto text-primary hover:underline shrink-0" onClick={() => toast("Los resúmenes y el asistente usan IA (AI Act, art. 50).")}>
          Más información
        </button>
      </div>
    </div>
  );
};
